import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { Plus, Loader2, Trash2, Edit3, Users as UsersIcon, Search, RefreshCw,
  Sparkles, CalendarCheck, Building2, Landmark, Receipt, Wallet, ShieldCheck, ChevronRight,
  Share2, Copy, Check, ExternalLink, Download, DollarSign, TrendingUp,
} from 'lucide-react';
import { Sidebar, Topbar } from './Dashboard';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { clients, auth, getStoredUser, clientService } from '../api';
import api from '../api';
import { downloadCSV } from '../utils/exports';
import { ManagementAccountsField } from '../components/ManagementAccountsField';
import { HealthBadge } from '../components/client/HealthBadge';

const SERVICES = ['Tax', 'Audit', 'Bookkeeping', 'Payroll', 'CIPC', 'VAT', 'Advisory'];
const ENTITY_TYPES = ['Pty Ltd', 'Ltd', 'CC', 'NPC', 'Trust', 'Sole Proprietor', 'Partnership'];
const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const VAT_CATEGORIES = [
  { value: 'A', label: 'Category A — bi-monthly (odd periods: Jan/Mar/May/Jul/Sep/Nov)' },
  { value: 'B', label: 'Category B — bi-monthly (even periods: Feb/Apr/Jun/Aug/Oct/Dec)' },
  { value: 'C', label: 'Category C — monthly' },
  { value: 'D', label: 'Category D — farming, 6-monthly' },
  { value: 'E', label: 'Category E — annual (aligned to FYE)' },
];

const DEFAULT_FORM = {
  name: '', contact: '', contactPhone: '', address: '', taxNumber: '', services: [],
  entityType: 'Pty Ltd', registrationNumber: '', registrationDate: '', financialYearEndMonth: 2,
  directors: [], owners: [], authorisedShares: 1000, issuedShares: 100,
  incomeTaxRegistered: true, provisionalTaxpayer: true,
  vatRegistered: false, vatCategory: 'B', vatNumber: '',
  payeRegistered: false, payeNumber: '', sdlRegistered: false, uifRegistered: false,
  cipcAnnualReturn: true, beneficialOwnershipRequired: true,
  monthlyRetainer: 0,
  managementAccounts: false, managementAccountsDueDay: null, billingRate: null,
  autoGenerateTasks: true,
};

function fmtDate(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
  } catch { return iso; }
}

function toDateInput(iso) {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (isNaN(d.getTime())) return '';
    return d.toISOString().slice(0, 10);
  } catch { return ''; }
}

export default function Clients() {
  const nav = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState(getStoredUser());
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState(DEFAULT_FORM);
  const [tab, setTab] = useState('basic');
  const [preview, setPreview] = useState([]);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const [health, setHealth] = useState({});

  const fetchAll = async () => {
    clientService.health().then(setHealth).catch(() => {});
    try {
      const [me, cl] = await Promise.all([auth.me().catch(() => null), clients.list()]);
      if (me) setUser(me);
      setList(cl);
      const openId = searchParams.get('open');
      const target = openId && cl.find((c) => c.id === openId);
      if (target) { openEdit(target); setSearchParams({}, { replace: true }); }
    } finally { setLoading(false); }
  };

  useEffect(() => {
    if (!localStorage.getItem('ad_token')) { nav('/login'); return; }
    fetchAll();
    // eslint-disable-next-line
  }, []);

  // Auto-preview generated tasks whenever compliance fields change
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(async () => {
      setPreviewLoading(true);
      try {
        const payload = { ...form };
        payload.registrationDate = form.registrationDate ? new Date(form.registrationDate).toISOString() : null;
        payload.financialYearEndMonth = form.financialYearEndMonth ? Number(form.financialYearEndMonth) : null;
        const res = await api.post('/clients/preview-tasks', payload);
        setPreview(res.data || []);
      } catch { setPreview([]); }
      finally { setPreviewLoading(false); }
    }, 350);
    return () => clearTimeout(t);
  }, [open, form.vatRegistered, form.vatCategory, form.payeRegistered, form.sdlRegistered, form.uifRegistered,
     form.cipcAnnualReturn, form.beneficialOwnershipRequired, form.registrationDate, form.financialYearEndMonth,
     form.provisionalTaxpayer, form.incomeTaxRegistered, form.entityType, form.name, form.managementAccounts, form.managementAccountsDueDay, form.autoGenerateTasks]);  // eslint-disable-line react-hooks/exhaustive-deps

  const openNew = () => {
    setEditing(null);
    setForm(DEFAULT_FORM);
    setPreview([]);
    setTab('basic');
    setOpen(true);
  };

  const openEdit = (c) => {
    setEditing(c);
    setForm({
      ...DEFAULT_FORM,
      ...c,
      registrationDate: toDateInput(c.registrationDate),
      services: c.services || [],
      financialYearEndMonth: c.financialYearEndMonth || 2,
      vatCategory: c.vatCategory || 'B',
    });
    setPreview([]);
    setTab('basic');
    setOpen(true);
  };

  const toggleService = (s) => {
    setForm((f) => ({ ...f, services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s] }));
  };

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const payload = { ...form };
      payload.registrationDate = form.registrationDate ? new Date(form.registrationDate).toISOString() : null;
      payload.financialYearEndMonth = form.financialYearEndMonth ? Number(form.financialYearEndMonth) : null;
      let result;
      if (editing) {
        result = await clients.update(editing.id, payload);
        setList((prev) => prev.map((c) => (c.id === editing.id ? result : c)));
      } else {
        result = await clients.create(payload);
        setList((prev) => [result, ...prev]);
      }
      toast({
        title: editing ? 'Client updated' : 'Client added',
        description: `${result.name} — ${preview.length} recurring task${preview.length === 1 ? '' : 's'} scheduled.`,
      });
      setOpen(false);
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally { setBusy(false); }
  };

  const remove = async (c) => {
    if (!window.confirm(`Delete ${c.name}? Pending auto-scheduled tasks will also be removed.`)) return;
    try {
      await clients.remove(c.id);
      setList((prev) => prev.filter((x) => x.id !== c.id));
      toast({ title: 'Client deleted', description: c.name });
    } catch (err) {
      toast({ title: 'Delete failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const regenerate = async (c) => {
    try {
      const res = await api.post(`/clients/${c.id}/regenerate-tasks`);
      toast({ title: 'Tasks refreshed', description: `${res.data.generated} new task${res.data.generated === 1 ? '' : 's'} scheduled for ${c.name}.` });
    } catch (err) {
      toast({ title: 'Refresh failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    }
  };

  const [portalClient, setPortalClient] = useState(null); // client whose portal-share dialog is open
  const [copied, setCopied] = useState(false);
  const [rotating, setRotating] = useState(false);

  const firmFull = (typeof user?.firm === 'object' ? user?.firm?.name : user?.firm) || user?.firmName || 'our team';
  const firmName = firmFull.replace(/\.$/, '');
  const portalMessage = (c, tok) => {
    const first = (c.contact || '').trim().split(' ')[0];
    return `Hi ${first || c.name},

We've set up a secure online client portal for ${c.name} with ${firmName}. You can use it any time to see your upcoming SARS and CIPC deadlines, check your invoices and payments, and keep track of what we're working on for you.

Here's your personal link:
${portalUrl(tok)}

Please keep this link private. It's unique to ${c.name}, and no login is needed. If you have any questions, simply reply to this message. We're always happy to help.

Kind regards,
${user?.name || ''}
${firmFull}`;
  };
  const [shareMsg, setShareMsg] = useState('');
  const [msgCopied, setMsgCopied] = useState(false);
  const openPortalShare = (c) => { setPortalClient(c); setCopied(false); setMsgCopied(false); setShareMsg(c.portalToken ? portalMessage(c, c.portalToken) : ''); };
  useEffect(() => {
    if (portalClient?.portalToken && !shareMsg) setShareMsg(portalMessage(portalClient, portalClient.portalToken));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portalClient?.portalToken]);
  const copyShareMsg = async () => {
    try {
      await navigator.clipboard.writeText(shareMsg);
      setMsgCopied(true);
      toast({ title: 'Message copied', description: 'Paste it into WhatsApp or email.' });
      setTimeout(() => setMsgCopied(false), 2500);
    } catch {
      toast({ title: 'Could not copy', description: 'Select the message and copy it manually.', variant: 'destructive' });
    }
  };
  const closePortalShare = () => setPortalClient(null);
  const portalUrl = (tok) => `${window.location.origin}/portal/${tok}`;

  const copyPortalLink = async () => {
    if (!portalClient?.portalToken) return;
    try {
      await navigator.clipboard.writeText(portalUrl(portalClient.portalToken));
      setCopied(true);
      toast({ title: 'Link copied', description: 'Share it with your client.' });
      setTimeout(() => setCopied(false), 2500);
    } catch {
      toast({ title: 'Could not copy', description: 'Copy the link manually.', variant: 'destructive' });
    }
  };

  const rotatePortalToken = async () => {
    if (!portalClient) return;
    if (!window.confirm('Rotate the portal link? The old link will stop working immediately.')) return;
    setRotating(true);
    try {
      const res = await clients.rotatePortalToken(portalClient.id);
      const updated = { ...portalClient, portalToken: res.portalToken };
      setPortalClient(updated);
      setList((prev) => prev.map((x) => (x.id === updated.id ? { ...x, portalToken: res.portalToken } : x)));
      setShareMsg(portalMessage(updated, res.portalToken));
      setCopied(false);
      toast({ title: 'Link rotated', description: 'A fresh secure link has been issued.' });
    } catch (err) {
      toast({ title: 'Rotate failed', description: err?.response?.data?.detail || 'Try again.', variant: 'destructive' });
    } finally {
      setRotating(false);
    }
  };

  const filtered = list.filter((c) =>
    !query || c.name.toLowerCase().includes(query.toLowerCase()) || (c.contact || '').toLowerCase().includes(query.toLowerCase())
  );

  const money = (n) => 'R ' + (Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 });
  const retainerMasked = list.some((c) => c._retainerMasked);
  const displayRetainer = (c) => c._retainerMasked ? '••••' : money(c.monthlyRetainer);

  const retainerStats = (() => {
    if (retainerMasked) return { total: null, count: null, avg: null, masked: true };
    const paying = list.filter((c) => Number(c.monthlyRetainer || 0) > 0);
    const total = paying.reduce((a, c) => a + Number(c.monthlyRetainer || 0), 0);
    return { total, count: paying.length, avg: paying.length ? total / paying.length : 0, masked: false };
  })();

  const exportAllToExcel = () => {
    const headers = [
      'Name', 'Entity type', 'Registration #', 'Registration date', 'FYE month',
      'Contact email', 'Contact phone', 'Address',
      'Tax number', 'Income tax', 'Provisional taxpayer',
      'VAT registered', 'VAT category', 'VAT number',
      'PAYE registered', 'PAYE number', 'SDL', 'UIF',
      'CIPC Annual Return', 'Beneficial ownership required',
      'Authorised shares', 'Issued shares',
      'Directors', 'Owners',
      'Services',
      'Monthly retainer (ZAR)',
      'Auto-generate tasks',
      'Portal link',
      'Created at',
    ];
    const yn = (v) => (v ? 'Yes' : 'No');
    const people = (list) => (list || []).map((p) => `${p.name || ''}${p.role ? ' (' + p.role + ')' : ''}${p.idNumber ? ' [' + p.idNumber + ']' : ''}${p.share ? ' — ' + p.share + '%' : ''}`).join('; ');
    const rows = list.map((c) => [
      c.name || '',
      c.entityType || '',
      c.registrationNumber || '',
      c.registrationDate ? new Date(c.registrationDate).toISOString().slice(0, 10) : '',
      c.financialYearEndMonth ? MONTHS[(c.financialYearEndMonth - 1) % 12] : '',
      c.contact || '',
      c.contactPhone || '',
      c.address || '',
      c.taxNumber || '',
      yn(c.incomeTaxRegistered),
      yn(c.provisionalTaxpayer),
      yn(c.vatRegistered),
      c.vatRegistered ? (c.vatCategory || '') : '',
      c.vatNumber || '',
      yn(c.payeRegistered),
      c.payeNumber || '',
      yn(c.sdlRegistered),
      yn(c.uifRegistered),
      yn(c.cipcAnnualReturn),
      yn(c.beneficialOwnershipRequired),
      c.authorisedShares ?? '',
      c.issuedShares ?? '',
      people(c.directors),
      people(c.owners),
      (c.services || []).join(', '),
      Number(c.monthlyRetainer || 0).toFixed(2),
      yn(c.autoGenerateTasks !== false),
      c.portalToken ? `${window.location.origin}/portal/${c.portalToken}` : '',
      c.createdAt ? new Date(c.createdAt).toISOString() : '',
    ]);
    const stamp = new Date().toISOString().slice(0, 10);
    downloadCSV(`accu-desk-clients-${stamp}`, headers, rows);
    toast({ title: 'Exported', description: `${list.length} client${list.length === 1 ? '' : 's'} downloaded as CSV.` });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-stone-50 grid place-items-center">
        <div className="flex items-center gap-2 text-stone-600"><Loader2 className="animate-spin" size={18}/> Loading clients...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 flex">
      <Sidebar activeLabel="Clients"/>
      <div className="flex-1 min-w-0">
        <Topbar user={user} firm={user?.firm}/>
        <main className="p-4 lg:p-8 space-y-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h1 className="text-2xl lg:text-3xl font-serif font-semibold text-stone-900">Clients</h1>
              <p className="mt-1 text-stone-600 text-sm">Capture compliance details once &mdash; the system auto-schedules every SARS &amp; CIPC deadline.</p>
            </div>
            <div className="flex gap-2">
              <div className="relative">
                <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400"/>
                <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search clients" className="pl-9 h-10 w-64"/>
              </div>
              <Button
                onClick={exportAllToExcel}
                variant="outline"
                disabled={list.length === 0}
                className="h-10 border-stone-300 text-stone-700 hover:text-sky-700 hover:border-sky-400"
                data-testid="export-clients-btn"
              >
                <Download size={16} className="mr-2"/> Export to Excel
              </Button>
              <Button onClick={openNew} className="bg-emerald-800 hover:bg-emerald-900 text-white h-10" data-testid="add-client-btn"><Plus size={16} className="mr-2"/> Add client</Button>
            </div>
          </div>

          {/* KPI strip */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="clients-kpis">
            <div className="rounded-xl bg-white border border-stone-200 p-5">
              <div className="flex items-center gap-2 text-stone-500 text-xs font-medium"><UsersIcon size={14}/> Total clients</div>
              <div className="mt-2 text-3xl font-serif font-semibold text-stone-900 tabular-nums" data-testid="kpi-total-clients">{list.length}</div>
              <div className="mt-1 text-xs text-stone-500">across your firm</div>
            </div>
            <div className="rounded-xl bg-white border border-stone-200 p-5">
              <div className="flex items-center gap-2 text-stone-500 text-xs font-medium"><DollarSign size={14}/> Monthly retainer total</div>
              <div className="mt-2 text-3xl font-serif font-semibold text-emerald-700 tabular-nums" data-testid="kpi-retainer-total">{retainerStats.masked ? '••••' : money(retainerStats.total)}</div>
              <div className="mt-1 text-xs text-stone-500">{retainerStats.masked ? 'hidden by your admin' : 'recurring MRR'}</div>
            </div>
            <div className="rounded-xl bg-white border border-stone-200 p-5">
              <div className="flex items-center gap-2 text-stone-500 text-xs font-medium"><Wallet size={14}/> On retainer</div>
              <div className="mt-2 text-3xl font-serif font-semibold text-stone-900 tabular-nums" data-testid="kpi-retainer-count">{retainerStats.masked ? '—' : retainerStats.count}</div>
              <div className="mt-1 text-xs text-stone-500">of {list.length} clients</div>
            </div>
            <div className="rounded-xl bg-white border border-stone-200 p-5">
              <div className="flex items-center gap-2 text-stone-500 text-xs font-medium"><TrendingUp size={14}/> Average retainer</div>
              <div className="mt-2 text-3xl font-serif font-semibold text-stone-900 tabular-nums" data-testid="kpi-retainer-avg">{retainerStats.masked ? '••••' : money(retainerStats.avg)}</div>
              <div className="mt-1 text-xs text-stone-500">{retainerStats.masked ? 'hidden by your admin' : 'per paying client'}</div>
            </div>
          </div>

          <div className="rounded-xl bg-white border border-stone-200 overflow-hidden">
            <div className="flex items-center justify-between p-5 border-b border-stone-100">
              <div className="flex items-center gap-2 text-stone-900 font-semibold"><UsersIcon size={18}/> All clients</div>
              <Badge variant="outline" className="border-stone-300">{filtered.length}</Badge>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-stone-50 text-stone-600 text-xs uppercase tracking-wider">
                  <tr>
                    <th className="text-left px-5 py-3 font-medium">Name</th>
                    <th className="hidden md:table-cell text-left px-5 py-3 font-medium">Health</th>
                    <th className="hidden md:table-cell text-left px-5 py-3 font-medium">Entity</th>
                    <th className="hidden md:table-cell text-left px-5 py-3 font-medium">Compliance</th>
                    <th className="hidden md:table-cell text-left px-5 py-3 font-medium">FYE</th>
                    <th className="hidden sm:table-cell text-right px-5 py-3 font-medium">Retainer</th>
                    <th className="px-5 py-3"></th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-stone-100">
                  {filtered.length === 0 && (
                    <tr><td colSpan={7} className="px-5 py-10 text-center text-stone-500">No clients {query ? 'match your search' : 'yet. Add your first client to get started.'}.</td></tr>
                  )}
                  {filtered.map((c) => (
                    <tr key={c.id} className="hover:bg-stone-50">
                      <td className="px-5 py-4">
                        <Link to={`/dashboard/clients/${c.id}`} className="font-medium text-stone-900 hover:text-emerald-800 hover:underline" data-testid={`client-link-${c.id}`}>{c.name}</Link>
                        <div className="text-xs text-stone-500">{c.contact || 'no contact email'}</div>
                      </td>
                      <td className="hidden md:table-cell px-5 py-4"><HealthBadge health={health[c.id]} testid={`client-health-${c.id}`}/></td>
                      <td className="hidden md:table-cell px-5 py-4">
                        <div className="text-stone-800">{c.entityType || '—'}</div>
                        <div className="text-xs text-stone-500">{c.registrationNumber || (c.taxNumber ? `Tax ${c.taxNumber}` : '—')}</div>
                      </td>
                      <td className="hidden md:table-cell px-5 py-4">
                        <div className="flex flex-wrap gap-1">
                          {c.vatRegistered && <span className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-100">VAT {c.vatCategory || 'B'}</span>}
                          {c.payeRegistered && <span className="text-[10px] px-1.5 py-0.5 rounded bg-blue-50 text-blue-800 border border-blue-100">PAYE</span>}
                          {c.provisionalTaxpayer && <span className="text-[10px] px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-100">Prov. Tax</span>}
                          {c.cipcAnnualReturn && <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-purple-800 border border-purple-100">CIPC AR</span>}
                          {c.beneficialOwnershipRequired && <span className="text-[10px] px-1.5 py-0.5 rounded bg-stone-100 text-stone-700">BO</span>}
                          {!c.vatRegistered && !c.payeRegistered && !c.provisionalTaxpayer && !c.cipcAnnualReturn && (
                            <span className="text-xs text-stone-400">—</span>
                          )}
                        </div>
                      </td>
                      <td className="hidden md:table-cell px-5 py-4 text-stone-700">{c.financialYearEndMonth ? MONTHS[(c.financialYearEndMonth - 1) % 12] : '—'}</td>
                      <td className="hidden sm:table-cell px-5 py-4 text-right tabular-nums">
                        {c._retainerMasked ? (
                          <span className="font-semibold text-stone-500" data-testid={`retainer-${c.id}`}>••••</span>
                        ) : Number(c.monthlyRetainer || 0) > 0 ? (
                          <span className="font-semibold text-emerald-700" data-testid={`retainer-${c.id}`}>{displayRetainer(c)}</span>
                        ) : (
                          <span className="text-xs text-stone-400">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-right space-x-1 whitespace-nowrap">
                        <button onClick={() => openPortalShare(c)} className="text-sky-700 sm:text-stone-500 hover:text-sky-700 p-1.5 sm:p-1" title="Share client portal link" data-testid={`share-portal-btn-${c.id}`}><Share2 size={17}/></button>
                        <button onClick={() => regenerate(c)} className="text-stone-500 hover:text-emerald-800 p-1" title="Re-schedule tasks"><RefreshCw size={15}/></button>
                        <button onClick={() => openEdit(c)} className="text-stone-500 hover:text-emerald-800 p-1" title="Edit"><Edit3 size={15}/></button>
                        <button onClick={() => remove(c)} className="text-stone-500 hover:text-red-600 p-1" title="Delete"><Trash2 size={15}/></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </main>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Building2 size={18} className="text-emerald-800"/>
              {editing ? `Edit ${editing.name || 'client'}` : 'Add a new client'}
            </DialogTitle>
            <div className="text-sm text-stone-500">Capture the compliance profile &mdash; recurring tasks will be auto-scheduled based on these settings.</div>
          </DialogHeader>
          <form onSubmit={save}>
            <div className="grid lg:grid-cols-3 gap-6">
              <div className="lg:col-span-2">
                <Tabs value={tab} onValueChange={setTab}>
                  <TabsList className="grid grid-cols-5 w-full">
                    <TabsTrigger value="basic">Basic</TabsTrigger>
                    <TabsTrigger value="entity">Entity</TabsTrigger>
                    <TabsTrigger value="people">People</TabsTrigger>
                    <TabsTrigger value="tax">Tax &amp; VAT</TabsTrigger>
                    <TabsTrigger value="payroll">Payroll &amp; CIPC</TabsTrigger>
                  </TabsList>

                  <TabsContent value="basic" className="pt-4 space-y-4">
                    <div>
                      <Label htmlFor="cname">Client name<span className="text-red-600">*</span></Label>
                      <Input id="cname" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="Sandton Holdings Ltd" className="mt-1.5"/>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="contact">Contact email</Label>
                        <Input id="contact" type="email" value={form.contact} onChange={(e) => setForm({ ...form, contact: e.target.value })} placeholder="finance@client.co.za" className="mt-1.5"/>
                      </div>
                      <div>
                        <Label htmlFor="phone">Contact phone</Label>
                        <Input id="phone" value={form.contactPhone} onChange={(e) => setForm({ ...form, contactPhone: e.target.value })} placeholder="+27 79 622 6431" className="mt-1.5"/>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="address">Address</Label>
                      <Input id="address" value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} placeholder="Registered address" className="mt-1.5"/>
                    </div>
                    {!retainerMasked && (
                    <div className="rounded-lg border border-emerald-100 bg-emerald-50/50 p-4">
                      <Label htmlFor="retainer" className="text-emerald-900">Monthly retainer <span className="text-stone-500 font-normal">(ZAR, optional)</span></Label>
                      <div className="mt-1.5 flex items-center gap-2">
                        <span className="text-sm text-stone-500 font-medium">R</span>
                        <Input
                          id="retainer"
                          type="number"
                          min="0"
                          step="50"
                          value={form.monthlyRetainer ?? 0}
                          onChange={(e) => setForm({ ...form, monthlyRetainer: Number(e.target.value) || 0 })}
                          placeholder="0"
                          className="max-w-[200px]"
                          data-testid="client-retainer-input"
                        />
                        <span className="text-xs text-stone-500">/ month</span>
                      </div>
                      <div className="mt-1.5 text-[11px] text-stone-500">Charged monthly regardless of hours — powers the "Recurring MRR" total on the Clients page.</div>
                    </div>
                    )}
                    <ManagementAccountsField form={form} setForm={setForm}/>
                    <div>
                      <Label htmlFor="billingRate">Client hourly rate override (R)</Label>
                      <Input id="billingRate" type="number" min="0" step="10" value={form.billingRate ?? ''} placeholder="Use each team member's rate"
                        onChange={(e) => setForm({ ...form, billingRate: e.target.value === '' ? null : Number(e.target.value) })}
                        className="mt-1.5" data-testid="client-billing-rate-input"/>
                      <p className="text-xs text-stone-500 mt-1">Leave empty to bill at each team member&apos;s own rate.</p>
                    </div>
                    <div>
                      <Label>Services</Label>
                      <div className="mt-1.5 flex flex-wrap gap-2">
                        {SERVICES.map((s) => (
                          <button key={s} type="button" onClick={() => toggleService(s)} className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${form.services.includes(s) ? 'bg-emerald-800 text-white border-emerald-800' : 'bg-white text-stone-700 border-stone-300 hover:border-emerald-500'}`}>{s}</button>
                        ))}
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="entity" className="pt-4 space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Entity type</Label>
                        <Select value={form.entityType || 'Pty Ltd'} onValueChange={(v) => setForm({ ...form, entityType: v, beneficialOwnershipRequired: ['Pty Ltd','Ltd','CC','NPC'].includes(v), cipcAnnualReturn: ['Pty Ltd','Ltd','CC','NPC'].includes(v) })}>
                          <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
                          <SelectContent>{ENTITY_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="regNo">CIPC registration number</Label>
                        <Input id="regNo" value={form.registrationNumber} onChange={(e) => setForm({ ...form, registrationNumber: e.target.value })} placeholder="2015/012345/07" className="mt-1.5"/>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="regDate">Registration date</Label>
                        <Input id="regDate" type="date" value={form.registrationDate} onChange={(e) => setForm({ ...form, registrationDate: e.target.value })} className="mt-1.5"/>
                        <div className="mt-1 text-xs text-stone-500">Used to schedule the annual return in the correct month.</div>
                      </div>
                      <div>
                        <Label>Financial year end</Label>
                        <Select value={String(form.financialYearEndMonth || 2)} onValueChange={(v) => setForm({ ...form, financialYearEndMonth: Number(v) })}>
                          <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
                          <SelectContent>{MONTHS.map((mo, i) => <SelectItem key={mo} value={String(i + 1)}>{mo}</SelectItem>)}</SelectContent>
                        </Select>
                        <div className="mt-1 text-xs text-stone-500">Drives IRP6 provisional and ITR14 income tax dates.</div>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="tax">Income tax number</Label>
                      <Input id="tax" value={form.taxNumber} onChange={(e) => setForm({ ...form, taxNumber: e.target.value })} placeholder="9012345678" className="mt-1.5"/>
                    </div>
                  </TabsContent>

                  <TabsContent value="people" className="pt-4 space-y-6">
                    <PeopleSection
                      title="Directors"
                      description="Captured for CIPC filings, SARS Power of Attorney and share certificates."
                      people={form.directors}
                      onChange={(v) => setForm({ ...form, directors: v })}
                      showShareholding={false}
                      defaultRole="Director"
                    />
                    <div className="grid grid-cols-2 gap-4 rounded-lg border border-stone-200 p-4 bg-stone-50/50">
                      <div>
                        <Label htmlFor="authShares">Authorised shares</Label>
                        <Input id="authShares" type="number" min={0} value={form.authorisedShares || 0} onChange={(e) => setForm({ ...form, authorisedShares: Number(e.target.value) })} className="mt-1.5"/>
                      </div>
                      <div>
                        <Label htmlFor="issuedShares">Issued shares</Label>
                        <Input id="issuedShares" type="number" min={0} value={form.issuedShares || 0} onChange={(e) => setForm({ ...form, issuedShares: Number(e.target.value) })} className="mt-1.5"/>
                      </div>
                    </div>
                    <PeopleSection
                      title="Shareholders / Beneficial Owners"
                      description="Feeds the share register, share certificates and beneficial-ownership declaration."
                      people={form.owners}
                      onChange={(v) => setForm({ ...form, owners: v })}
                      showShareholding
                      defaultRole="Shareholder"
                    />
                  </TabsContent>

                  <TabsContent value="tax" className="pt-4 space-y-4">
                    <ToggleRow icon={Landmark} title="Income tax registered" desc="Adds ITR14 12 months after every FYE." checked={form.incomeTaxRegistered} onChange={(v) => setForm({ ...form, incomeTaxRegistered: v })}/>
                    <ToggleRow icon={Receipt} title="Provisional taxpayer" desc="Adds IRP6 for both provisional periods per year." checked={form.provisionalTaxpayer} onChange={(v) => setForm({ ...form, provisionalTaxpayer: v })}/>
                    <div className="border-t border-stone-200 pt-4"/>
                    <ToggleRow icon={ShieldCheck} title="VAT registered" desc="Adds VAT201 based on the SARS category below." checked={form.vatRegistered} onChange={(v) => setForm({ ...form, vatRegistered: v })}/>
                    {form.vatRegistered && (
                      <div className="grid grid-cols-1 gap-4 pl-11">
                        <div>
                          <Label>VAT category</Label>
                          <Select value={form.vatCategory || 'B'} onValueChange={(v) => setForm({ ...form, vatCategory: v })}>
                            <SelectTrigger className="mt-1.5"><SelectValue/></SelectTrigger>
                            <SelectContent>{VAT_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}</SelectContent>
                          </Select>
                        </div>
                        <div>
                          <Label htmlFor="vatNo">VAT number</Label>
                          <Input id="vatNo" value={form.vatNumber} onChange={(e) => setForm({ ...form, vatNumber: e.target.value })} placeholder="4123456789" className="mt-1.5"/>
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  <TabsContent value="payroll" className="pt-4 space-y-4">
                    <ToggleRow icon={Wallet} title="PAYE registered" desc="Adds EMP201 every month, plus EMP501 mid-year and annual reconciliations." checked={form.payeRegistered} onChange={(v) => setForm({ ...form, payeRegistered: v })}/>
                    {form.payeRegistered && (
                      <div className="pl-11">
                        <Label htmlFor="payeNo">PAYE number</Label>
                        <Input id="payeNo" value={form.payeNumber} onChange={(e) => setForm({ ...form, payeNumber: e.target.value })} placeholder="7123456789" className="mt-1.5"/>
                      </div>
                    )}
                    <div className="grid grid-cols-2 gap-4">
                      <ToggleRow compact icon={Wallet} title="SDL registered" checked={form.sdlRegistered} onChange={(v) => setForm({ ...form, sdlRegistered: v })}/>
                      <ToggleRow compact icon={Wallet} title="UIF registered" checked={form.uifRegistered} onChange={(v) => setForm({ ...form, uifRegistered: v })}/>
                    </div>
                    <div className="border-t border-stone-200 pt-4"/>
                    <ToggleRow icon={Building2} title="CIPC annual return" desc="Adds the annual return in the month of registration." checked={form.cipcAnnualReturn} onChange={(v) => setForm({ ...form, cipcAnnualReturn: v })}/>
                    <ToggleRow icon={ShieldCheck} title="Beneficial ownership filing" desc="Adds annual BO filing alongside the CIPC annual return." checked={form.beneficialOwnershipRequired} onChange={(v) => setForm({ ...form, beneficialOwnershipRequired: v })}/>
                    <div className="border-t border-stone-200 pt-4"/>
                    <ToggleRow icon={Sparkles} title="Auto-generate recurring tasks" desc="Turn off if you want to schedule tasks manually." checked={form.autoGenerateTasks} onChange={(v) => setForm({ ...form, autoGenerateTasks: v })}/>
                  </TabsContent>
                </Tabs>
              </div>

              <aside className="lg:col-span-1">
                <div className="rounded-xl bg-emerald-50/60 border border-emerald-100 p-4 sticky top-2">
                  <div className="flex items-center gap-2 text-emerald-900 font-semibold text-sm">
                    <CalendarCheck size={16}/> Auto-scheduled tasks
                  </div>
                  <div className="mt-1 text-xs text-emerald-900/80">Next 6 months, refreshed as you change settings.</div>
                  <div className="mt-4 space-y-2 max-h-72 overflow-y-auto pr-1">
                    {previewLoading && <div className="flex items-center gap-2 text-xs text-emerald-900/70"><Loader2 size={12} className="animate-spin"/> Calculating...</div>}
                    {!previewLoading && preview.length === 0 && (
                      <div className="text-xs text-emerald-900/70 italic">No tasks yet &mdash; enable a compliance obligation to see it here.</div>
                    )}
                    {!previewLoading && preview.map((t, i) => (
                      <div key={i} className="rounded-lg bg-white border border-emerald-100 px-3 py-2 text-xs">
                        <div className="flex items-start justify-between gap-2">
                          <div className="font-medium text-stone-900 leading-tight flex-1">{t.title}</div>
                          {t.category && <span className="text-[9px] font-medium px-1.5 py-0.5 rounded bg-stone-100 text-stone-700 whitespace-nowrap flex-shrink-0">{t.category}</span>}
                        </div>
                        <div className="mt-0.5 text-stone-500 flex items-center gap-1"><ChevronRight size={11}/> Due {fmtDate(t.dueDate)}</div>
                      </div>
                    ))}
                  </div>
                  {!previewLoading && preview.length > 0 && (
                    <div className="mt-4 pt-3 border-t border-emerald-100 text-xs text-emerald-900">
                      <strong>{preview.length}</strong> task{preview.length === 1 ? '' : 's'} will be scheduled on save.
                    </div>
                  )}
                </div>
              </aside>
            </div>

            <DialogFooter className="mt-6">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={busy} className="bg-emerald-800 hover:bg-emerald-900 text-white">
                {busy ? <Loader2 size={16} className="mr-2 animate-spin"/> : <Sparkles size={14} className="mr-2"/>}
                {editing ? 'Save & re-schedule tasks' : 'Add client & schedule tasks'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Portal share dialog */}
      <Dialog open={!!portalClient} onOpenChange={(v) => !v && closePortalShare()}>
        <DialogContent className="sm:max-w-lg max-h-[92vh] overflow-y-auto" data-testid="portal-share-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Share2 size={18} className="text-sky-700"/>
              Share client portal
            </DialogTitle>
          </DialogHeader>
          {portalClient && (
            <div className="space-y-4">
              <div className="rounded-lg bg-sky-50 border border-sky-100 p-3 text-xs text-sky-900">
                <div className="flex items-start gap-2">
                  <ShieldCheck size={14} className="mt-0.5 flex-shrink-0"/>
                  <div>
                    <div className="font-medium">Read-only, secure link.</div>
                    <div className="mt-0.5 text-sky-800/80">Anyone with this link can see <span className="font-semibold">{portalClient.name}</span>&apos;s outstanding SARS &amp; CIPC filings and invoices — nothing else.</div>
                  </div>
                </div>
              </div>

              <div>
                <Label className="text-xs">Portal link</Label>
                <div className="mt-1.5 flex gap-2">
                  <Input
                    readOnly
                    value={portalClient.portalToken ? portalUrl(portalClient.portalToken) : 'Generating…'}
                    onFocus={(e) => e.target.select()}
                    className="font-mono text-xs bg-stone-50"
                    data-testid="portal-link-input"
                  />
                  <Button type="button" onClick={copyPortalLink} className={`h-10 ${copied ? 'bg-emerald-700 hover:bg-emerald-700' : 'bg-emerald-800 hover:bg-emerald-900'} text-white`} data-testid="portal-copy-btn">
                    {copied ? <><Check size={14} className="mr-1.5"/> Copied</> : <><Copy size={14} className="mr-1.5"/> Copy</>}
                  </Button>
                </div>
              </div>

              {portalClient.portalToken && (
                <div>
                  <div className="flex items-center justify-between">
                    <Label htmlFor="portal-share-msg" className="text-xs">Message to send (edit if you like)</Label>
                    <button type="button" onClick={() => setShareMsg(portalMessage(portalClient, portalClient.portalToken))} className="text-xs text-stone-500 hover:text-emerald-800" data-testid="portal-message-reset-btn">Reset</button>
                  </div>
                  <textarea id="portal-share-msg" value={shareMsg} onChange={(e) => setShareMsg(e.target.value)} rows={11}
                    className="mt-1.5 w-full rounded-md border border-stone-200 bg-white p-3 text-sm leading-relaxed text-stone-800 focus:outline-none focus:ring-2 focus:ring-emerald-700/30" data-testid="portal-message-textarea"/>
                  <Button type="button" onClick={copyShareMsg} className={`mt-2 w-full h-10 ${msgCopied ? 'bg-emerald-700 hover:bg-emerald-700' : 'bg-emerald-800 hover:bg-emerald-900'} text-white`} data-testid="portal-copy-message-btn">
                    {msgCopied ? <><Check size={14} className="mr-1.5"/> Message copied</> : <><Copy size={14} className="mr-1.5"/> Copy message with link</>}
                  </Button>
                </div>
              )}

              <div className="flex items-center gap-2 pt-1">
                {portalClient.portalToken && (
                  <a
                    href={portalUrl(portalClient.portalToken)}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm text-sky-700 hover:text-sky-800 font-medium"
                    data-testid="portal-preview-link"
                  >
                    Preview as client <ExternalLink size={13}/>
                  </a>
                )}
                <div className="ml-auto">
                  <Button type="button" variant="outline" onClick={rotatePortalToken} disabled={rotating} data-testid="portal-rotate-btn">
                    {rotating ? <Loader2 size={14} className="mr-1.5 animate-spin"/> : <RefreshCw size={13} className="mr-1.5"/>} Rotate link
                  </Button>
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={closePortalShare}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function ToggleRow({ icon: Icon, title, desc, checked, onChange, compact }) {
  return (
    <div className={`flex ${compact ? 'items-center' : 'items-start'} gap-3 ${compact ? '' : 'py-1'}`}>
      <div className={`h-9 w-9 rounded-lg bg-stone-100 text-stone-700 grid place-items-center flex-shrink-0 ${checked ? 'bg-emerald-100 text-emerald-800' : ''}`}>
        <Icon size={16}/>
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-stone-900 text-sm">{title}</div>
        {desc && <div className="text-xs text-stone-500 mt-0.5">{desc}</div>}
      </div>
      <Switch checked={!!checked} onCheckedChange={onChange}/>
    </div>
  );
}

function PeopleSection({ title, description, people, onChange, showShareholding, defaultRole }) {
  const add = () => onChange([...(people || []), { name: '', idNumber: '', role: defaultRole || '', email: '', phone: '', shareholdingPercent: 0, shareCount: 0, shareClass: 'Ordinary' }]);
  const remove = (i) => onChange(people.filter((_, idx) => idx !== i));
  const update = (i, patch) => onChange(people.map((p, idx) => (idx === i ? { ...p, ...patch } : p)));
  const totalShareholding = (people || []).reduce((a, p) => a + Number(p.shareholdingPercent || 0), 0);

  return (
    <div className="rounded-lg border border-stone-200 bg-white overflow-hidden">
      <div className="flex items-center justify-between p-4 border-b border-stone-100">
        <div>
          <div className="font-semibold text-stone-900 text-sm">{title}</div>
          {description && <div className="text-xs text-stone-500 mt-0.5">{description}</div>}
        </div>
        <div className="flex items-center gap-3">
          {showShareholding && people.length > 0 && (
            <span className={`text-xs font-medium ${totalShareholding === 100 ? 'text-emerald-700' : 'text-amber-700'}`}>
              Total: {totalShareholding}%
            </span>
          )}
          <button type="button" onClick={add} className="text-xs font-medium text-emerald-800 hover:text-emerald-900 inline-flex items-center gap-1">
            + Add person
          </button>
        </div>
      </div>
      <div className="divide-y divide-stone-100">
        {people.length === 0 && (
          <div className="p-4 text-xs text-stone-500 italic">No {title.toLowerCase()} captured yet. Click <strong>+ Add person</strong> to start.</div>
        )}
        {people.map((p, i) => (
          <div key={i} className="p-4 space-y-3 relative bg-stone-50/30">
            <div className="flex items-center justify-between">
              <div className="text-xs font-medium text-stone-500">#{i + 1}</div>
              <button type="button" onClick={() => remove(i)} className="text-xs text-red-600 hover:text-red-800">Remove</button>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Full name</Label>
                <Input value={p.name || ''} onChange={(e) => update(i, { name: e.target.value })} placeholder="Jane Kruger" className="mt-1 h-9 text-sm"/>
              </div>
              <div>
                <Label className="text-xs">ID / Passport number</Label>
                <Input value={p.idNumber || ''} onChange={(e) => update(i, { idNumber: e.target.value })} placeholder="8003130001080" className="mt-1 h-9 text-sm"/>
              </div>
              <div>
                <Label className="text-xs">Role</Label>
                <Input value={p.role || ''} onChange={(e) => update(i, { role: e.target.value })} placeholder="Director / Public Officer" className="mt-1 h-9 text-sm"/>
              </div>
              <div>
                <Label className="text-xs">Email</Label>
                <Input type="email" value={p.email || ''} onChange={(e) => update(i, { email: e.target.value })} placeholder="jane@company.co.za" className="mt-1 h-9 text-sm"/>
              </div>
              {showShareholding && (
                <>
                  <div>
                    <Label className="text-xs">Shareholding %</Label>
                    <Input type="number" min={0} max={100} step="0.01" value={p.shareholdingPercent || 0} onChange={(e) => update(i, { shareholdingPercent: Number(e.target.value) })} className="mt-1 h-9 text-sm"/>
                  </div>
                  <div>
                    <Label className="text-xs">Shares held</Label>
                    <Input type="number" min={0} value={p.shareCount || 0} onChange={(e) => update(i, { shareCount: Number(e.target.value) })} className="mt-1 h-9 text-sm"/>
                  </div>
                </>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
